import {Component,Input,OnDestroy,OnInit,ViewChild} from '@angular/core';
import {Prestation} from "@domain/prestation/prestation";
import {Pays} from "@domain/geographie/pays";
import {TranslateService} from "@ngx-translate/core";
import {PrestationService} from "@services/admin/frais/prestation.service";
import {first} from "rxjs/operators";
import {PuissanceFiscale} from "@domain/admin/entreprise/vehicules/puissanceFiscale";
import {TypeDistance} from "@domain/prestation/unite";
import {NgForm} from "@angular/forms";
import {PrestationDetailService} from "@components/admin/frais/prestations/prestation-detail/prestation-detail.service";
import {Subscription} from "rxjs";
import {NatureVoyage} from "@domain/prestation/nature-voyage";
import {ListView} from "@domain/common/list-view";
import {FiltreListItemComponent} from "@components/admin/frais/prestations/prestation-detail/filtres/filtre-list-item.component";
import {PrestFiltre,TypeFiltreChar} from "@domain/prestation/prest-filtre";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {PopinAjoutFiltresComponent} from "@components/admin/frais/prestations/prestation-detail/filtres/popin-ajout-filtres/popin-ajout-filtres.component";
import {ToastrService} from "ngx-toastr";
import {handleResult} from "@share/utils/handle-result";
import {TypeFiltreDigit} from "@domain/admin/entreprise/analytique/filtre-option";

/**
 * Composant de l'onglet filtres d'une prestation
 */
@Component({
	selector: 'prestation-filtres',
	host: {'data-test-id': 'prestation-filtres'},
	templateUrl: './prestation-filtres.component.html'
})
export class PrestationFiltresComponent implements OnInit, OnDestroy {
	/** Type distance */
	protected readonly TypeDistance = TypeDistance;

	/** Prestation concernée */
	private _prestation: Prestation;
	@Input() set prestation(prestation: Prestation) { this._prestation = prestation; this.syncPrestation(); };
	get prestation(): Prestation { return this._prestation; }

	/** Liste des pays possibles */
	listePays: Pays[] = [{
		idPays: -1,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.pays.tousLesPays")
	} as Pays,{
		idPays: -2,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.pays.tousLesPaysSauf")
	} as Pays];

	/** Liste des puissances fiscales possibles */
	listePuissanceFiscale: PuissanceFiscale[] = [{
		idPuissance: 0,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.aucune")
	} as PuissanceFiscale];

	/** Liste des familles charge possibles */
	listeFamilleCharge: { code: NatureVoyage, libelle: string }[] = [{
		code: null,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.aucune")
	},{
		code: NatureVoyage.AVION,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.familleCharge.A")
	},{
		code: NatureVoyage.TRAIN,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.familleCharge.T")
	},{
		code: NatureVoyage.VOITURE,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.familleCharge.R")
	},{
		code: NatureVoyage.HOTEL,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.familleCharge.H")
	},{
		code: NatureVoyage.DIVERS,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.familleCharge.D")
	},{
		code: NatureVoyage.FRAIS,
		libelle: this.translateService.instant("admin.frais.prestations.detail.filtres.generiques.familleCharge.F")
	}];

	/** Famille charge sélectionnée */
	selectedFamilleCharge:{ code: NatureVoyage, libelle: string };

	/** Liste des filtres visibilité */
	listeVisibilite: ListView<PrestFiltre,FiltreListItemComponent>;

	/** Liste des filtres import */
	listeImport: ListView<PrestFiltre,FiltreListItemComponent>;

	/** Liste des filtres mobiles */
	listeMobile: ListView<PrestFiltre,FiltreListItemComponent>;

	/** Formulaire du composant */
	@ViewChild('form') form: NgForm;

	/** Souscription */
	souscription: Subscription;

	/**
	 * Constructeur
	 */
	constructor(private prestationService: PrestationService,
				private prestationDetailService: PrestationDetailService,
				private matDialog: MatDialog,
				private toastrService: ToastrService,
				private translateService: TranslateService) {}

	/**
	 * Initialisation du composant
	 */
	ngOnInit() {
		//Récupération des pays possibles
		this.prestationService.listePays().pipe(first()).subscribe(result => {
			//Stockage des pays
			this.listePays = this.listePays.concat(result.data?.listePays as Pays[]);

			//Obtention du pays de l'entreprise et mise à jour du libellé concerné
			this.listePays[1].libelle += result.data?.paysEntreprise;
		});

		//Récupération des puissances fiscales possibles
		this.prestationService.listePuissanceFiscale().pipe(first()).subscribe(l => {
			//Stockage des puissances fiscales
			this.listePuissanceFiscale = this.listePuissanceFiscale.concat(l);

			//Synchronisation de la puissance fiscale
			this.syncPuissanceFiscale();
		});

		//Abonnement aux évènements de mise à jour des données imbriquées
		this.souscription = this.prestationDetailService.nestedDataUpdatedEvent$.subscribe(() => this.syncPrestation());

		//Définition de la liste des filtres de visibilité
		this.listeVisibilite = new ListView<PrestFiltre,FiltreListItemComponent>({
			uri: `/controller/AdminPrestation/listeFiltreVisibilite/${this.prestation.idPrestation}`,
			title: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.visibilite'),
			component: FiltreListItemComponent,
			isSearchBar: false,
			isFilter: false,
			isFilterVisible: false,
			isLocal: true,
			extraOptions: {
				refreshListe: () => this.listeVisibilite.refresh(),
				idPrestation: this.prestation.idPrestation,
				options: [{
					libelle: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.' + TypeFiltreChar.TYPE_CATEGORIE),
					valeur: TypeFiltreDigit.FILTRE_CATEGORIE,
					avatar: "CA",
					libelleListe: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.caListeTitre')
				},{
					libelle: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.' + TypeFiltreChar.TYPE_MOT_CLE),
					valeur: TypeFiltreDigit.FILTRE_MOT_CLE,
					avatar: "MC",
					libelleListe: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.mcListeTitre')
				},{
					libelle: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.' + TypeFiltreChar.TYPE_ORGANISATION),
					valeur: TypeFiltreDigit.FILTRE_ORGANISATION,
					avatar: "SE",
					libelleListe: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.seListeTitre')
				},{
					libelle: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.' + TypeFiltreChar.TYPE_TYPE_ENTITE),
					valeur: TypeFiltreDigit.FILTRE_TYPE_ENTITE,
					avatar: "TE",
					libelleListe: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.teListeTitre')
				}]
			},
			listeActions: [
				{
					icon: 'add',
					onPress: () => this.addFiltre(this.listeVisibilite)
				}
			],
			emptyMessage: 'admin.frais.prestations.detail.filtres.listeFiltres.noData'
		});

		//Forçage du nombre par page
		this.listeVisibilite.nbObjetsParPage = 5;

		//Message de liste vide
		this.listeVisibilite.emptyMessage = 'admin.frais.prestations.detail.filtres.listeFiltres.noData';

		//Définition de la liste des filtres d'import
		this.listeImport = new ListView<PrestFiltre,FiltreListItemComponent>({
			uri: `/controller/AdminPrestation/listeFiltreImport/${this.prestation.idPrestation}`,
			title: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.import'),
			component: FiltreListItemComponent,
			isSearchBar: false,
			isFilter: false,
			isFilterVisible: false,
			isLocal: true,
			extraOptions: {
				refreshListe: () => this.listeImport.refresh(),
				idPrestation: this.prestation.idPrestation,
				options: [{
					libelle: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.' + TypeFiltreChar.TYPE_CODE_ACTIVITE),
					valeur: TypeFiltreDigit.FILTRE_CODE_ACTIVITE,
					avatar: "CA",
					libelleListe: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.ca2ListeTitre')
				},{
					libelle: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.' + TypeFiltreChar.TYPE_TYPE_CHARGE),
					valeur: TypeFiltreDigit.FILTRE_TYPE_CHARGE,
					avatar: "TC",
					libelleListe: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.tcListeTitre')
				}]
			},
			listeActions: [
				{
					icon: 'add',
					onPress: () => this.addFiltre(this.listeImport)
				}
			],
			emptyMessage: 'admin.frais.prestations.detail.filtres.listeFiltres.noData'
		});

		//Forçage du nombre par page
		this.listeImport.nbObjetsParPage = 5;

		//Message de liste vide
		this.listeImport.emptyMessage = 'admin.frais.prestations.detail.filtres.listeFiltres.noData';

		//Définition de la liste des filtres mobiles
		this.listeMobile = new ListView<PrestFiltre,FiltreListItemComponent>({
			uri: `/controller/AdminPrestation/listeFiltreMobile/${this.prestation.idPrestation}`,
			title: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.mobiles'),
			component: FiltreListItemComponent,
			isSearchBar: false,
			isFilter: false,
			isFilterVisible: false,
			isLocal: true,
			extraOptions: {
				refreshListe: () => this.listeMobile.refresh(),
				idPrestation: this.prestation.idPrestation,
				options: [{
					libelle: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.' + TypeFiltreChar.TYPE_RUBRIQUE),
					valeur: TypeFiltreDigit.FILTRE_RUBRIQUE,
					avatar: "RU",
					libelleListe: this.translateService.instant('admin.frais.prestations.detail.filtres.listeFiltres.ruListeTitre')
				}]
			},
			listeActions: [
				{
					icon: 'add',
					onPress: () => this.addFiltre(this.listeMobile)
				}
			],
			emptyMessage: 'admin.frais.prestations.detail.filtres.listeFiltres.noData'
		});

		//Forçage du nombre par page
		this.listeMobile.nbObjetsParPage = 5;

		//Message de liste vide
		this.listeMobile.emptyMessage = 'admin.frais.prestations.detail.filtres.listeFiltres.noData';
	}

	/**
	 * Synchronisations lors de la mise à jour de la prestation
	 */
	private syncPrestation() {
		//Synchronisation de la puissance fiscale
		this.syncPuissanceFiscale();

		//Synchronisation de la famille de charge
		this.syncFamilleCharge();
	}

	/**
	 * Synchronisation de la puissance fiscale
	 */
	private syncPuissanceFiscale() {
		//Si aucune puissance fiscale n'est renseignée ou que l'unité n'est pas une distance
		if (!this.prestation?.idPuissance
			|| (this.prestation?.unite?.typeDistance != TypeDistance.TYPE_DISTANCE_KM && this.prestation?.unite?.typeDistance != TypeDistance.TYPE_DISTANCE_MILES)) {
			//On choisit la première puissance fiscale
			this.prestation.idPuissance = this.listePuissanceFiscale[0].idPuissance;
		}
	}

	/**
	 * Synchronisation de la famille de charge
	 */
	private syncFamilleCharge() {
		//Si aucune famille de charge n'est renseignée
		if (!this.prestation?.natureVoyage) {
			//On choisit la première famille de charge
			this.selectedFamilleCharge = this.listeFamilleCharge[0];
		} else {
			//Sinon, on choisit la famille de charge
			this.selectedFamilleCharge = this.listeFamilleCharge.find(f => f.code === this.prestation.natureVoyage);
		}
	}

	/**
	 * Mise à jour de la famille de charge dans l'objet suite à une sélection
	 */
	onFamilleChargeChange() {
		//Mise à jour de la famille charge
		this.prestation.natureVoyage = this.selectedFamilleCharge.code;
	}

	/**
	 * Méthode appelée lors de l'ajout d'un filtre
	 */
	addFiltre(liste: ListView<PrestFiltre,FiltreListItemComponent>) {
		//Affichage de la fenêtre d'ajout d'un filtre
		this.matDialog.open(PopinAjoutFiltresComponent, {
			width: '40%',
			data: {
				liste: liste,
				idPrestation: this.prestation.idPrestation
			}
		}).afterClosed().subscribe((filtre: PrestFiltre) => {
			//Si la fenêtre est fermée après l'ajout d'un filtre
			if (filtre !== null && filtre.idFiltre) {
				//Sauvegarde du filtre sur la prestation
				this.prestationService.savePrestationFiltre(this.prestation.idPrestation,filtre)
										.pipe(handleResult(this.translateService,this.toastrService,'global.success.enregistrement','global.errors.enregistrement'))
										.subscribe(() => liste.extraOptions.refreshListe());
			}
		});
	}

	/**
	 * Destruction du composant
	 */
	ngOnDestroy() {
		//Désabonnement de la souscription
		this.souscription?.unsubscribe();
	}
}